"use client";

import type { ReactNode, FC, Ref } from "react";
import { mergeClasses } from "@huub-web/utils";

import { Card } from "../Card";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { Button, type ButtonProps } from "../Button";
import { Icon } from "../Icon";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";
import { mergeRefs } from "../../utils";

import { containerStyle, contentStyle } from "./OTDCard.css";

export type OTDCardProps = {
  /**
   * ID
   */
  id: string;
  /**
   * Card title
   */
  title: ReactNode;
  /**
   * Card description
   */
  description: ReactNode;
  /**
   * Button label text
   */
  buttonValue: ReactNode;
  /**
   * Label for ContentMenu registration
   */
  label: string;
  /**
   * Callback when the button is clicked
   */
  onClick?: ButtonProps["onClick"];
  /**
   * Whether the card is disabled
   */
  disabled?: boolean;
  /**
   * Additional class name
   */
  className?: string;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * A card for displaying an OTD (Opdracht tot Dienstverlening / Service Agreement)
 * call-to-action with a title, description, and action button.
 */
export const OTDCard: FC<OTDCardProps> = ({
  id,
  title,
  description,
  buttonValue,
  label,
  onClick,
  disabled,
  className,
  ref,
}) => {
  const sectionRef = useContentMenuSection<HTMLDivElement>({
    id,
    label,
    forcePosition: "leadingInside",
    icon: "signature",
  });

  return (
    <Card
      ref={mergeRefs(ref, sectionRef)}
      variant="infoSurface"
      padding="none"
      borderRadius="s"
      className={mergeClasses(className, containerStyle)}
      border="transparent"
      onClick={disabled ? undefined : onClick}
      style={{ cursor: disabled ? undefined : "pointer" }}
    >
      <Flex className={mergeClasses(contentStyle)} direction="column" gap="s">
        <Text variant="displayS" bold>
          {title}
        </Text>
        <Text variant="bodyS" color="currentColor50">
          {description}
        </Text>
        <Flex grow align="flex-end">
          <Button
            disabled={disabled}
            leading={<Icon icon="signature" size="s" />}
          >
            {buttonValue}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};
